import {idFromGlobalId} from '../../../../forecast-app/shared/util/GlobalIdUtil';
import {encodeCompanyId} from '../featureFlagRows';
import {RolloutDirectory} from '../rollout/featureFlagRolloutDirectory';

export interface CohortView {
	/** Encoded global id — what a mutation targets. */
	cohortId: string;
	rawCohortId: number;
	name: string;
	/** Encoded company ids, so membership can be tested against the rest of the feature directly. */
	memberCompanyIds: string[];
}

export interface MembershipEntry {
	cohortNames: string[];
	isBeta: boolean;
	isAlwaysLast: boolean;
}

export type MembershipIndex = Map<string, MembershipEntry>;

export interface MembershipRow {
	companyId: string;
	companyName: string;
	usersLabel: string;
	/** The "Also in" cell. */
	note: string;
	/** Whether `note` names the always-last list, which is what the warning colour marks. */
	notesAlwaysLast: boolean;
}

/**
 * The list whose tab is on screen.
 *
 * Every row on a tab belongs to that tab's own list by construction, so naming it again in the
 * "Also in" cell tells the operator nothing. All three lists need excluding, not just cohorts.
 */
export type ViewedList = {kind: 'cohort'; name: string} | {kind: 'beta'} | {kind: 'alwaysLast'} | null;

export interface CompanyPickerOption {
	id: string;
	/** Raw company name. Also what the ComboBox filters on. */
	name: string;
	/** Rendered beside the name rather than baked into it, so searching for "users" does not match every company. */
	usersLabel: string;
}

/** Cohort shape needed to build the tabs — a structural subset of the Relay query result. */
interface CohortSource {
	/** Encoded global id — what a mutation targets. */
	readonly id: string | null;
	/** Raw integer id, sent alongside the encoded id so nothing has to decode one to get the other. */
	readonly cohortId: number | null;
	readonly name: string | null;
	readonly companyIds: ReadonlyArray<number | null> | null;
}

const EMPTY_LABEL = '—';
const BETA_LABEL = 'Beta program';
const ALWAYS_LAST_LABEL = 'Always last';

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

/** Something identifying to call a company the directory has no name for, matching the assignment table. */
function fallbackCompanyName(encodedCompanyId: string): string {
	const numericId = idFromGlobalId(encodedCompanyId);
	return `Company ${Number.isNaN(numericId) ? encodedCompanyId : numericId}`;
}

function userCountLabel(count: number): string {
	return count === 1 ? '1 user' : `${count} users`;
}

// Mirrors FEATURE_FLAG_LIST_REJECTED in graphql's FeatureFlagListErrorUtil, and the two packages
// cannot import from each other, so it is repeated rather than shared. Changing it on either side
// fails that side's tests, which is the prompt to change it here too — the cost of getting it wrong
// is that a stated reason silently reverts to an opaque "Internal Error" modal.
const REJECTION_MARKER = 'FEATURE_FLAG_LIST_REJECTED: ';
const TRAILING_CORRELATION_ID = /\s*\(([^()]*)\)\s*$/;

/**
 * Reads the backend's own validation message out of a rejected cohort create/rename, or returns null
 * when the error is not one of ours — in which case the caller must fall back to the standard error
 * handling rather than swallowing it.
 *
 * The arriving text is Relay's stringified error, so the reason sits somewhere inside a larger message
 * rather than at its start. Mission Control is untranslated, so the message is returned verbatim —
 * there is no code-to-translation-key lookup the way the generic mutation error handling has.
 */
export const parseCohortRejectionMessage = (error: string): string | null => {
	const at = error.indexOf(REJECTION_MARKER);
	if (at === -1) {
		return null;
	}

	// The reason runs to the end of its line; Relay appends its own context on later lines.
	const [firstLine] = error.slice(at + REJECTION_MARKER.length).split('\n');
	const withId = firstLine.match(TRAILING_CORRELATION_ID);
	const reason = withId ? firstLine.slice(0, withId.index) : firstLine;
	return reason.trim();
};

/**
 * Cohorts in the order the sidebar lists them: by name, case-insensitively.
 *
 * Sorted rather than left in insertion order so a newly created cohort lands somewhere its creator
 * can predict, and so the order does not depend on what the backend happened to return.
 */
export function buildCohortViews(cohorts: ReadonlyArray<CohortSource | null> | null | undefined): CohortView[] {
	return (cohorts ?? [])
		.filter(isPresent)
		.filter(cohort => cohort.id != null)
		.map(cohort => ({
			cohortId: String(cohort.id),
			rawCohortId: cohort.cohortId ?? NaN,
			name: cohort.name ?? '',
			memberCompanyIds: (cohort.companyIds ?? []).filter(isPresent).map(encodeCompanyId),
		}))
		.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
}

/**
 * The always-last list, encoded the way grants and the eligible pool are.
 *
 * The query returns raw numeric ids; everything that tests membership against them — the manage-lists
 * screen and the rollout modal's percentage fill — works in encoded ids. Shared rather than derived
 * per screen so the two cannot drift on what is on the list.
 *
 * Order is left exactly as it arrived. Unlike cohorts, this list is never rendered as itself; its
 * callers either sort their own rows or only test membership.
 */
export function buildAlwaysLastCompanyIds(alwaysLastCompanyIds: ReadonlyArray<number | null> | null | undefined): string[] {
	return (alwaysLastCompanyIds ?? []).filter(isPresent).map(encodeCompanyId);
}

/**
 * Every list each company belongs to, keyed by encoded company id.
 *
 * Built once per render and shared by all three tabs. Deriving it per tab is how the three would
 * drift into disagreeing about what a company is in.
 */
export function buildMembershipIndex({
	cohorts,
	alwaysLastCompanyIds,
	betaCompanyIds,
}: {
	cohorts: readonly CohortView[];
	alwaysLastCompanyIds: readonly string[];
	betaCompanyIds: readonly string[];
}): MembershipIndex {
	const index: MembershipIndex = new Map();

	const entryFor = (companyId: string): MembershipEntry => {
		const existing = index.get(companyId);
		if (existing) {
			return existing;
		}
		const created: MembershipEntry = {cohortNames: [], isBeta: false, isAlwaysLast: false};
		index.set(companyId, created);
		return created;
	};

	cohorts.forEach(cohort => {
		cohort.memberCompanyIds.forEach(companyId => entryFor(companyId).cohortNames.push(cohort.name));
	});
	betaCompanyIds.forEach(companyId => {
		entryFor(companyId).isBeta = true;
	});
	alwaysLastCompanyIds.forEach(companyId => {
		entryFor(companyId).isAlwaysLast = true;
	});

	return index;
}

/**
 * The "Also in" cell for one company.
 *
 * `viewedList` drops the list currently being viewed, so its own members' cells describe what else
 * they are in rather than restating where they already are.
 */
export function buildMembershipLabel(entry: MembershipEntry | undefined, viewedList: ViewedList): string {
	if (entry == null) {
		return EMPTY_LABEL;
	}
	const excludedCohortName = viewedList?.kind === 'cohort' ? viewedList.name : null;
	const names: string[] = [];
	if (entry.isBeta && viewedList?.kind !== 'beta') {
		names.push(BETA_LABEL);
	}
	entry.cohortNames.filter(name => name !== excludedCohortName).forEach(name => names.push(name));
	if (entry.isAlwaysLast && viewedList?.kind !== 'alwaysLast') {
		names.push(ALWAYS_LAST_LABEL);
	}
	return names.length === 0 ? EMPTY_LABEL : names.join(', ');
}

/**
 * Table rows for a set of companies, sorted by company name case-insensitively.
 *
 * Sorted rather than left in the backend's insertion order — matching how `buildCohortViews` sorts
 * cohorts — so a cohort's membership lists in an order the operator can predict, and a newly added
 * company does not simply land at the bottom.
 */
export function buildMembershipRows(
	companyIds: readonly string[],
	index: MembershipIndex,
	directory: RolloutDirectory,
	viewedList: ViewedList
): MembershipRow[] {
	return companyIds
		.map(companyId => ({
			companyId,
			companyName: directory.companyNames.get(companyId) || fallbackCompanyName(companyId),
			usersLabel: userCountLabel(directory.personsByCompanyId.get(companyId)?.length ?? 0),
			note: buildMembershipLabel(index.get(companyId), viewedList),
			notesAlwaysLast: (index.get(companyId)?.isAlwaysLast ?? false) && viewedList?.kind !== 'alwaysLast',
		}))
		.sort((a, b) => a.companyName.toLocaleLowerCase().localeCompare(b.companyName.toLocaleLowerCase()));
}

/** Every company not already on the list being edited, sorted by name. */
export function buildCompanyPickerOptions(directory: RolloutDirectory, excludeCompanyIds: Set<string>): CompanyPickerOption[] {
	return Array.from(directory.companyNames.entries())
		.filter(([companyId]) => !excludeCompanyIds.has(companyId))
		.map(([companyId, name]) => ({
			id: companyId,
			name: name || fallbackCompanyName(companyId),
			usersLabel: userCountLabel(directory.personsByCompanyId.get(companyId)?.length ?? 0),
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
}
